import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';

const ContactHub = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('quick');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    urgency: 'normal',
    preferredContact: 'phone'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const serviceOptions = [
    { value: 'carpet-cleaning', label: 'Carpet Cleaning' },
    { value: 'floor-cleaning', label: 'Floor Cleaning' },
    { value: 'deep-cleaning', label: 'Deep Cleaning' },
    { value: 'commercial-cleaning', label: 'Commercial Cleaning' },
    { value: 'emergency-service', label: 'Emergency Service' },
    { value: 'other', label: 'Other Service' }
  ];

  const urgencyOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High Priority' },
    { value: 'emergency', label: 'Emergency' }
  ];

  const contactOptions = [
    { value: 'phone', label: 'Phone Call' },
    { value: 'email', label: 'Email' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'text', label: 'Text Message' }
  ];

  const toggleHub = () => {
    setIsOpen(!isOpen);
  };

  const closeHub = () => {
    setIsOpen(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = () => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event?.target?.files);
    console.log('Files selected:', files);
    // Handle file upload logic here
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Form submitted:', formData);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        urgency: 'normal',
        preferredContact: 'phone'
      });
      
      // Show success message or redirect
      alert('Thank you! We\'ll contact you shortly.');
      closeHub();
    } catch (error) {
      console.error('Submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent(
      `Hi Clean360! I'm interested in ${formData?.service || 'your services'}. ${formData?.message || 'Please contact me for more information.'}`
    );
    window.open(`https://wa.me/1234567890?text=${message}`, '_blank');
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:+1234567890';
  };

  return (
    <>
      {/* Contact Hub Toggle Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <Button
          variant="default"
          size="lg"
          onClick={toggleHub}
          className="rounded-full shadow-elevation-4 hover:shadow-elevation-5 transition-all duration-300 hover:scale-105"
          aria-label="Contact us"
        >
          <Icon name={isOpen ? "X" : "MessageCircle"} size={24} />
          {!isOpen && <span className="ml-2 hidden sm:inline">Contact Us</span>}
        </Button>
      </div>
      {/* Contact Hub Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-md max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text-primary">
                Get In Touch
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeHub}
                aria-label="Close contact form"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('quick')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                  activeTab === 'quick' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                Quick Contact
              </button>
              <button
                onClick={() => setActiveTab('detailed')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                  activeTab === 'detailed' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                Detailed Request
              </button>
            </div>

            {/* Content */}
            <div className="p-4 max-h-96 overflow-y-auto">
              {activeTab === 'quick' ? (
                /* Quick Contact Tab */
                (<div className="space-y-4">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-medium text-text-primary mb-2">
                      Need Help Right Away?
                    </h3>
                    <p className="text-sm text-text-secondary">
                      Choose your preferred contact method
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={handleEmergencyCall}
                      iconName="Phone"
                      iconPosition="left"
                      className="h-12"
                    >
                      Call Now
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleWhatsAppContact}
                      iconName="MessageSquare"
                      iconPosition="left"
                      className="h-12"
                    >
                      WhatsApp
                    </Button>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-2 text-sm text-text-secondary">
                      <Icon name="Clock" size={16} />
                      <span>Available 24/7 for emergencies</span>
                    </div>
                  </div>
                  <div className="border-t border-border pt-4">
                    <h4 className="font-medium text-text-primary mb-3">
                      Or send a quick message:
                    </h4>
                    <div className="space-y-3">
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={formData?.name}
                        onChange={(e) => handleInputChange('name', e?.target?.value)}
                      />
                      <Input
                        type="tel"
                        placeholder="Phone number"
                        value={formData?.phone}
                        onChange={(e) => handleInputChange('phone', e?.target?.value)}
                      />
                      <Input
                        type="text"
                        placeholder="Brief message"
                        value={formData?.message}
                        onChange={(e) => handleInputChange('message', e?.target?.value)}
                      />
                      <Button
                        variant="default"
                        fullWidth
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        iconName="Send"
                        iconPosition="left"
                      >
                        Send Message
                      </Button>
                    </div>
                  </div>
                </div>)
              ) : (
                /* Detailed Request Tab */
                (<form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Name"
                      type="text"
                      required
                      value={formData?.name}
                      onChange={(e) => handleInputChange('name', e?.target?.value)}
                    />
                    <Input
                      label="Phone"
                      type="tel"
                      required
                      value={formData?.phone}
                      onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    />
                  </div>
                  <Input
                    label="Email"
                    type="email"
                    required
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                  />
                  <Select
                    label="Service Needed"
                    options={serviceOptions}
                    value={formData?.service}
                    onChange={(value) => handleInputChange('service', value)}
                    placeholder="Select a service"
                    required
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      label="Urgency"
                      options={urgencyOptions}
                      value={formData?.urgency}
                      onChange={(value) => handleInputChange('urgency', value)}
                    />
                    <Select
                      label="Preferred Contact"
                      options={contactOptions}
                      value={formData?.preferredContact}
                      onChange={(value) => handleInputChange('preferredContact', value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Message
                    </label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                      placeholder="Describe your cleaning needs..."
                      value={formData?.message}
                      onChange={(e) => handleInputChange('message', e?.target?.value)}
                    />
                  </div>
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleFileUpload}
                      iconName="Paperclip"
                      iconPosition="left"
                      className="w-full"
                    >
                      Attach Photos (Optional)
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    loading={isSubmitting}
                    iconName="Send"
                    iconPosition="left"
                  >
                    Submit Request
                  </Button>
                </form>)
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactHub;